"use client";

import { motion, useReducedMotion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { memo } from "react";

interface ScrollFadeInProps {
    children: React.ReactNode;
    duration?: number;
    delay?: number;
    threshold?: number;
    triggerOnce?: boolean;
    className?: string;
    direction?: "up" | "down" | "left" | "right";
    distance?: number;
}

const getDirectionOffset = (direction: ScrollFadeInProps["direction"], distance: number) => {
    switch (direction) {
        case "up":
            return { y: distance };
        case "down":
            return { y: -distance };
        case "left":
            return { x: distance };
        case "right":
            return { x: -distance };
        default:
            return { y: distance };
    }
};

const ScrollFadeIn = memo(({
    children,
    duration = 0.5,
    delay = 0,
    threshold = 0.2,
    triggerOnce = true,
    className = "",
    direction = "up",
    distance = 30
}: ScrollFadeInProps) => {
    // Respect user's reduced motion preference
    const prefersReducedMotion = useReducedMotion();

    const { ref, inView } = useInView({
        triggerOnce,
        threshold,
        rootMargin: "-50px 0px"
    });

    const directionOffset = getDirectionOffset(direction, distance);

    const variants = {
        hidden: {
            opacity: 0,
            ...directionOffset,
        },
        visible: {
            opacity: 1,
            x: 0,
            y: 0,
            transition: {
                duration: prefersReducedMotion ? 0 : duration,
                delay,
                ease: [0.25, 0.1, 0.25, 1], // Improved easing curve
            },
        },
    };

    // If user prefers reduced motion, just show the content
    if (prefersReducedMotion) {
        return <div className={className}>{children}</div>;
    }

    return (
        <motion.div
            ref={ref}
            variants={variants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className={className}
            style={{ willChange: "opacity, transform" }}
        >
            {children}
        </motion.div>
    );
});

ScrollFadeIn.displayName = 'ScrollFadeIn';

export default ScrollFadeIn;
